import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { SEO } from "../components/shared/SEO";
import { PageHeader } from "../components/shared/PageHeader";
import { Reveal, ImageReveal } from "../components/shared/Reveal";
import { CTABand } from "../components/shared/Sections";
import { api, imgUrl } from "../lib/api";

const SEA = "https://images.unsplash.com/photo-1439405326854-014607f694d7?crop=entropy&cs=srgb&fm=jpg&q=80&w=2400";
const fmt = (d) => new Date(d).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" });

export default function DiarioDiBordo() {
  const { data: posts = [], isLoading } = useQuery({ queryKey: ["posts"], queryFn: () => api.get("/posts").then((r) => r.data) });
  return (
    <>
      <SEO title="Diario di Bordo Immobiliare | Home Haven" description="Analisi, aggiornamenti e contenuti sullo sviluppo immobiliare, i cantieri e il metodo operativo di Home Haven." image={SEA} />
      <PageHeader eyebrow="Appunti di rotta" title="Diario di bordo" image={SEA} text="Articoli, riflessioni ed estratti del libro: il racconto del viaggio che unisce mare, impresa e sviluppo immobiliare." />
      <section className="section bg-cream">
        <div className="container-hh">
          {isLoading && <div className="text-stone text-sm uppercase tracking-widest">Caricamento…</div>}
          {!isLoading && posts.length === 0 && <p className="lead" data-testid="posts-empty">I primi articoli del Diario di bordo sono in preparazione.</p>}
          <div data-testid="posts-grid" className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-x-16 md:gap-y-20">
            {posts.map((p, i) => (
              <Reveal key={p.id} delay={(i % 2) * 0.1} className={i % 2 === 1 ? "md:mt-16" : ""}>
                <Link to={`/diario-di-bordo/${p.slug}`} data-testid={`post-card-${p.slug}`} className="group block">
                  {p.cover_image && <ImageReveal src={imgUrl(p.cover_image)} alt={p.title} className="aspect-[16/10] bg-beige" />}
                  <div className="mt-6"><div className="eyebrow !text-stone">{p.category} — {fmt(p.published_at)}</div><h3 className="h-card mt-2 group-hover:text-gold transition-colors">{p.title}</h3><p className="mt-3 text-sm md:text-base font-light text-ink/70 leading-relaxed">{p.excerpt}</p></div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      <CTABand dark eyebrow="Il libro" title="Scopri La Rotta del Valore." primary={{ to: "/la-rotta-del-valore", label: "Vai al libro", testId: "diario-cta-libro" }} />
    </>
  );
}

export function DiarioPost() {
  const { slug } = useParams();
  const { data: p, isLoading } = useQuery({ queryKey: ["post", slug], queryFn: () => api.get(`/posts/${slug}`).then((r) => r.data) });
  if (isLoading) return <div className="min-h-screen bg-navy" />;
  if (!p) return <section className="min-h-[70vh] bg-navy text-cream flex items-center"><div className="container-hh pt-32"><h1 className="h-display">Articolo non trovato.</h1><Link to="/diario-di-bordo" className="btn-light mt-10">Diario di bordo</Link></div></section>;
  return (
    <>
      <SEO title={`${p.title} | Diario di Bordo Home Haven`} description={p.excerpt || p.content.slice(0, 160)} image={p.cover_image ? imgUrl(p.cover_image) : SEA} />
      <PageHeader compact eyebrow={`${p.category} — ${fmt(p.published_at)}`} title={p.title} image={p.cover_image ? imgUrl(p.cover_image) : SEA} />
      <section className="section bg-cream">
        <article className="container-hh max-w-3xl" data-testid="post-content">
          <nav aria-label="Breadcrumb" data-testid="post-breadcrumb" className="mb-10 flex flex-wrap items-center gap-2 text-xs uppercase tracking-[0.2em] text-stone"><Link to="/" className="hover:text-gold">Home</Link><span aria-hidden="true">/</span><Link to="/diario-di-bordo" className="hover:text-gold inline-flex items-center gap-2"><ArrowLeft size={14} /> Diario di bordo</Link><span aria-hidden="true">/</span><span className="text-ink" aria-current="page">{p.title}</span></nav>
          {p.excerpt && <p className="font-serif italic text-2xl md:text-3xl font-light text-navy mb-10">{p.excerpt}</p>}
          {p.content.split("\n").filter(Boolean).map((para, i) => <p key={i} className="lead mb-5">{para}</p>)}
          <aside className="mt-14 pt-8 border-t border-ink/10" data-testid="post-related-links">
            <div className="eyebrow mb-4">Approfondisci</div>
            <div className="flex flex-wrap gap-x-8 gap-y-3">
              <Link to="/progetti" className="btn-link">I progetti Home Haven</Link>
              <Link to="/cosa-facciamo" className="btn-link">Cosa facciamo</Link>
              <Link to="/la-rotta-del-valore" className="btn-link">Il libro La Rotta del Valore</Link>
            </div>
          </aside>
        </article>
      </section>
    </>
  );
}
